import { useState, type FormEvent, type ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUPPLIER_TRADE_LABELS,
  formatAmount,
  formatNzDate,
  formatRef,
  nzDate,
  parseAmount,
  parseRef,
  type Money,
} from '@promanage/core';
import {
  MAINTENANCE_JOB_STATE_LABELS,
  MAINTENANCE_URGENCY_LABELS,
  MAX_OWNER_APPROVAL_NOTE_LENGTH,
  MAX_WORK_ORDER_INSTRUCTIONS_LENGTH,
  MIN_OWNER_APPROVAL_NOTE_LENGTH,
  WORK_ORDER_STATE_LABELS,
  maintenanceQuoteExpired,
  maintenanceResponseOverdue,
  ownerApprovalRequired,
} from '@promanage/core/maintenance';
import {
  Amount,
  Button,
  Chip,
  Field,
  Input,
  Panel,
  PanelHeader,
  Ref,
  Select,
  Textarea,
} from '@promanage/ui';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import { DialogActions, FormDialog } from '../components/FormDialog';
import { RecordActions } from '../components/RecordActions';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordSkeleton } from '../components/RecordSkeleton';
import type {
  MaintenanceJobDetail,
  MaintenanceQuote,
  MaintenanceWorkOrder,
} from '../features/maintenance/types';
import { trpc } from '../lib/trpc';

/**
 * One maintenance job, addressed by its display ref: /maintenance/MN000003.
 *
 * Same ref contract as /bills/$ref — `beforeLoad` normalises anything ref-shaped to the canonical
 * form, and the component answers each remaining wrong shape distinctly.
 *
 * THE PAGE IS A LADDER, and it only ever offers the next rung: a reported job offers quotes and an
 * approval; an approved one offers a work order; an order out offers attendance, completion and a
 * recall; a finished job offers the sign-off. The API refuses out-of-order calls anyway — this is
 * what stops the screen presenting a button whose only outcome is a refusal.
 *
 * THE AUTHORITY IS SHOWN THREE WAYS on purpose: approved, out with contractors, and left. An
 * operator about to send a second tradesperson sees the third number without doing the
 * subtraction, because the server refuses on it.
 *
 * THE VARIANCE IS SHOWN AND NOT ENFORCED. An invoice over the authorised limit is flagged, because
 * the work is done and the money is owed — refusing to record an over-spend does not un-spend it.
 *
 * SHORT ON SCREEN, WHY IN HERE. The rules each rung follows used to be paragraphs on the page; an
 * operator who runs this ladder all day reads them once. The figures, the chips and the refusal
 * sentences the server writes carry the rest.
 */
export const Route = createFileRoute('/maintenance/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'maintenance') {
      const canonical = formatRef('maintenance', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({ to: '/maintenance/$ref', params: { ref: canonical }, replace: true });
      }
    }
  },
  component: MaintenanceDetailPage,
});

function MaintenanceDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  const record = useQuery(
    trpc.maintenance.get.queryOptions(
      { refSeq: parsed?.sequence ?? 0 },
      { enabled: parsed?.entity === 'maintenance', staleTime: 10_000 },
    ),
  );

  if (parsed === null || parsed.entity !== 'maintenance') {
    return (
      <NotFound
        title={parsed === null ? `“${ref}” is not a record reference` : 'That is not a job'}
      >
        {parsed === null
          ? 'A maintenance reference looks like MN000003.'
          : `${ref} belongs to another kind of record. Maintenance references start with MN.`}
      </NotFound>
    );
  }

  if (record.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <RecordSkeleton />
      </div>
    );
  }

  if (record.error || !record.data) {
    return (
      <NotFound title="No such job">{`${ref} is not a maintenance job in this agency.`}</NotFound>
    );
  }

  return <Loaded job={record.data} />;
}

type Tone = 'neutral' | 'accent' | 'positive' | 'warn' | 'danger';

const STATE_TONE: Record<MaintenanceJobDetail['state'], Tone> = {
  reported: 'warn',
  quoting: 'neutral',
  approved: 'accent',
  in_progress: 'accent',
  completed: 'positive',
  cancelled: 'neutral',
};

const URGENCY_TONE: Record<MaintenanceJobDetail['urgency'], Tone> = {
  emergency: 'danger',
  urgent: 'warn',
  routine: 'neutral',
  planned: 'neutral',
};

const ORDER_TONE: Record<MaintenanceWorkOrder['state'], Tone> = {
  issued: 'accent',
  attended: 'accent',
  completed: 'positive',
  recalled: 'neutral',
};

type Dialog = 'quote' | 'order' | 'complete' | 'cancel' | { recall: MaintenanceWorkOrder } | null;

function Loaded({ job }: { job: MaintenanceJobDetail }) {
  const queryClient = useQueryClient();
  const today = nzDate(new Date());
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.maintenance.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.maintenance.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.maintenance.summary.queryKey() });
  };

  const reopen = useMutation(
    trpc.maintenance.reopen.mutationOptions({
      meta: { errorTitle: 'Could not reopen that job' },
      onSuccess: refresh,
    }),
  );

  const open = job.state !== 'completed' && job.state !== 'cancelled';
  const approved = job.approvedLimitCents !== null;
  const late = open && maintenanceResponseOverdue(job.responseDueOn, job.orderedOn, today);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={job.summary}
        subtitle={`${job.propertyAddress} · ${SUPPLIER_TRADE_LABELS[job.trade]}`}
        actions={
          <>
            <Link to="/maintenance">
              <Button size="sm" variant="secondary">
                All jobs
              </Button>
            </Link>
            {open ? (
              <>
                <Button size="sm" variant="secondary" onClick={() => setDialog('complete')}>
                  Mark complete
                </Button>
                {/* Calling a job off is rarer than finishing one, so it waits behind More. */}
                <RecordActions
                  label="Cancel job"
                  pending={false}
                  onSelect={() => setDialog('cancel')}
                />
              </>
            ) : job.state === 'completed' ? (
              <RecordActions
                label="Reopen — the fault came back"
                pending={reopen.isPending}
                onSelect={() => reopen.mutate({ jobId: job.id })}
              />
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <RecordIdentity
          title={
            <Link
              to="/properties/$ref"
              params={{ ref: job.propertyRef }}
              className="hover:text-accent hover:underline"
            >
              {job.propertyAddress}
            </Link>
          }
          symbol={<Icon name="maintenance" className="size-6" />}
          status={
            <>
              <Ref entity="maintenance" sequence={job.refSeq} />
              <Chip tone={STATE_TONE[job.state]}>{MAINTENANCE_JOB_STATE_LABELS[job.state]}</Chip>
              <Chip tone={URGENCY_TONE[job.urgency]}>
                {MAINTENANCE_URGENCY_LABELS[job.urgency]}
              </Chip>
              {late ? <Chip tone="warn">Past target</Chip> : null}
            </>
          }
          subtitle={
            job.tenancyRef === null ? (
              'No tenancy — vacant or owner-instructed'
            ) : (
              <>
                Tenancy{' '}
                <Link
                  to="/tenancies/$ref"
                  params={{ ref: job.tenancyRef }}
                  className="font-medium text-ink hover:text-accent hover:underline"
                >
                  {job.tenancyRef}
                </Link>
              </>
            )
          }
          facts={[
            {
              label: 'Reported',
              value: (
                <>
                  {formatNzDate(job.reportedOn)}
                  {job.reportedByPersonName ? (
                    <span className="block font-normal text-ink-muted">
                      {job.reportedByPersonName}
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              label: 'Respond by',
              value:
                job.responseDueOn === null ? (
                  <span className="font-normal text-ink-subtle">No target</span>
                ) : (
                  <span className={late ? 'text-warn' : undefined}>
                    {formatNzDate(job.responseDueOn)}
                  </span>
                ),
            },
            {
              label: 'Entered by',
              value: job.reportedByName ?? (
                <span className="font-normal text-ink-subtle">Unknown</span>
              ),
            },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            {job.details || job.accessNotes ? <JobDetails job={job} /> : null}
            <Quotes
              job={job}
              today={today}
              open={open}
              onAdd={() => setDialog('quote')}
              onDone={refresh}
            />
            <WorkOrders
              job={job}
              today={today}
              // Nothing left means the server would refuse any order, so the button is not offered.
              canIssue={open && approved && BigInt(job.remainingCents ?? '0') > 0n}
              onIssue={() => setDialog('order')}
              onRecall={(order) => setDialog({ recall: order })}
              onDone={refresh}
            />
            <Invoices job={job} />
          </div>

          <div className="flex flex-col gap-4">
            <Spend job={job} />
            {job.state === 'cancelled' ? (
              <Panel className="p-5">
                <PanelHeader
                  title="Cancelled"
                  subtitle={job.cancelledByName ? `By ${job.cancelledByName}` : undefined}
                  className="p-0 pb-2"
                />
                <p className="text-[0.8125rem] text-ink-muted">{job.cancelledReason}</p>
              </Panel>
            ) : job.state === 'completed' ? (
              <Panel className="p-5">
                <PanelHeader
                  title={`Completed ${formatNzDate(job.completedOn ?? today)}`}
                  subtitle={
                    job.completedByName ? `Signed off by ${job.completedByName}` : undefined
                  }
                  className="p-0 pb-2"
                />
                {job.completionNotes ? (
                  <p className="text-[0.8125rem] whitespace-pre-line text-ink-muted">
                    {job.completionNotes}
                  </p>
                ) : null}
              </Panel>
            ) : !approved ? (
              <Authorise job={job} today={today} onDone={refresh} />
            ) : null}
          </div>
        </div>
      </div>

      {dialog === 'quote' ? (
        <QuoteDialog job={job} today={today} onClose={close} onDone={refresh} />
      ) : dialog === 'order' ? (
        <IssueOrderDialog job={job} today={today} onClose={close} onDone={refresh} />
      ) : dialog === 'complete' ? (
        <CompleteDialog job={job} today={today} onClose={close} onDone={refresh} />
      ) : dialog === 'cancel' ? (
        <CancelDialog job={job} onClose={close} onDone={refresh} />
      ) : dialog !== null ? (
        <RecallDialog order={dialog.recall} onClose={close} onDone={refresh} />
      ) : null}
    </div>
  );
}

function JobDetails({ job }: { job: MaintenanceJobDetail }) {
  return (
    <Panel className="p-5">
      <PanelHeader title="The problem" className="p-0 pb-3" />
      {job.details ? (
        <p className="max-w-prose text-[0.8125rem] whitespace-pre-line text-ink">{job.details}</p>
      ) : null}
      {job.accessNotes ? (
        <div className={`rounded-md bg-surface-sunk px-3 py-2 ${job.details ? 'mt-3' : ''}`}>
          <p className="text-[0.75rem] text-ink-muted">Access</p>
          <p className="mt-0.5 text-[0.8125rem] whitespace-pre-line text-ink">{job.accessNotes}</p>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Approved, out, left, invoiced — "left" shown rather than left to be worked out, because it is the
 * number the server refuses a work order on.
 *
 * WHAT THE RULE WAS AT THE TIME comes from the job's own snapshot, not the agency's current
 * setting, so a job approved in March still says what March's agreement allowed.
 */
function Spend({ job }: { job: MaintenanceJobDetail }) {
  if (job.approvedLimitCents === null) {
    return (
      <Panel className="p-5">
        <p className="text-[0.75rem] text-ink-muted">Authorised</p>
        <p className="mt-1 text-[1.375rem] font-extrabold tracking-[-0.02em] text-ink-subtle">
          Not yet
        </p>
      </Panel>
    );
  }

  return (
    <Panel className="p-5">
      <p className="text-[0.75rem] text-ink-muted">
        Authorised{job.approvedByName ? ` by ${job.approvedByName}` : ''}
      </p>
      <Amount
        value={BigInt(job.approvedLimitCents)}
        currency
        className="mt-1 block text-[1.75rem] font-extrabold tracking-[-0.02em] text-ink"
      />

      <dl className="mt-4 grid grid-cols-3 gap-3 rounded-md bg-surface-sunk px-3 py-2.5">
        <SpendFigure label="Out" cents={job.committedCents} />
        <SpendFigure label="Left" cents={job.remainingCents ?? '0'} />
        <SpendFigure label="Invoiced" cents={job.invoicedCents} warn={job.over} />
      </dl>

      {job.over ? (
        <p className="mt-3 text-[0.75rem] font-medium text-warn">
          <Amount value={BigInt(job.overCents)} currency tone="warn" /> over what was authorised.
        </p>
      ) : null}

      {job.ownerApprovedOn !== null ? (
        <div className="mt-3 border-t border-rule pt-3 text-[0.75rem] text-ink-muted">
          <p>
            Owner agreed {formatNzDate(job.ownerApprovedOn)} · over the{' '}
            <Amount
              value={BigInt(job.ownerApprovalThresholdCents ?? '0')}
              currency
              tone="neutral"
            />{' '}
            limit
          </p>
          {job.ownerApprovalNote ? (
            <p className="mt-1 whitespace-pre-line text-ink">“{job.ownerApprovalNote}”</p>
          ) : null}
        </div>
      ) : job.ownerApprovalThresholdCents !== null ? (
        <p className="mt-3 border-t border-rule pt-3 text-[0.75rem] text-ink-muted">
          Within the{' '}
          <Amount value={BigInt(job.ownerApprovalThresholdCents)} currency tone="neutral" /> the
          agency may approve alone
        </p>
      ) : null}
    </Panel>
  );
}

function SpendFigure({ label, cents, warn }: { label: string; cents: string; warn?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.6875rem] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-[0.8125rem] font-semibold">
        <Amount value={BigInt(cents)} currency tone={warn ? 'warn' : 'neutral'} />
      </dd>
    </div>
  );
}

/** The prices on the table, cheapest first, with the one that was ordered marked. */
function Quotes({
  job,
  today,
  open,
  onAdd,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  open: boolean;
  onAdd: () => void;
  onDone: () => void;
}) {
  const withdraw = useMutation(
    trpc.maintenance.withdrawQuote.mutationOptions({
      meta: { errorTitle: 'Could not withdraw that quote' },
      onSuccess: onDone,
    }),
  );

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Quotes"
        subtitle="Cheapest first"
        className="p-0 pb-3"
        aside={
          open ? (
            <Button size="sm" variant="secondary" onClick={onAdd}>
              <span aria-hidden="true">+</span> Add quote
            </Button>
          ) : undefined
        }
      />

      {job.quoteList.length === 0 ? (
        <p className="text-[0.8125rem] text-ink-muted">No quotes. Most jobs don’t need one.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {job.quoteList.map((quote) => (
            <QuoteRow
              key={quote.id}
              quote={quote}
              today={today}
              canWithdraw={open}
              withdrawing={withdraw.isPending}
              onWithdraw={() => withdraw.mutate({ quoteId: quote.id })}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function QuoteRow({
  quote,
  today,
  canWithdraw,
  onWithdraw,
  withdrawing,
}: {
  quote: MaintenanceQuote;
  today: string;
  canWithdraw: boolean;
  onWithdraw: () => void;
  withdrawing: boolean;
}) {
  const withdrawn = quote.withdrawnAt !== null;
  const expired = !withdrawn && maintenanceQuoteExpired(quote.validUntil, today);

  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule pb-2 last:border-0 last:pb-0">
      <div className="min-w-0">
        <Link
          to="/maintenance/contractors/$ref"
          params={{ ref: quote.supplierRef }}
          className={
            withdrawn
              ? 'font-medium text-ink-subtle line-through'
              : 'font-medium hover:text-accent hover:underline'
          }
        >
          {quote.supplierName}
        </Link>
        <span className="text-[0.8125rem] text-ink-muted"> {formatNzDate(quote.quotedOn)}</span>
        {quote.ordered ? (
          <Chip tone="accent" className="ml-2">
            Ordered
          </Chip>
        ) : null}
        {withdrawn ? (
          <Chip tone="neutral" className="ml-2">
            Withdrawn
          </Chip>
        ) : null}
        {expired ? (
          <Chip tone="warn" className="ml-2">
            Price expired
          </Chip>
        ) : null}
        {quote.scope ? (
          <span className="block truncate text-[0.75rem] text-ink-subtle">{quote.scope}</span>
        ) : null}
      </div>

      <div className="flex items-baseline gap-3">
        <Amount
          value={BigInt(quote.amountCents)}
          currency
          tone={withdrawn ? 'neutral' : 'accent'}
        />
        {canWithdraw && !withdrawn ? (
          <button
            type="button"
            onClick={onWithdraw}
            disabled={withdrawing}
            className="text-[0.75rem] text-ink-muted hover:text-danger hover:underline"
          >
            Withdraw
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Who was sent, what they were authorised to spend, and when they turned up.
 *
 * The attendance and completion buttons stamp TODAY — an operator marking a visit the moment the
 * contractor rings is the case these are for. The server takes any date; the button is the fast
 * path, not the only one.
 */
function WorkOrders({
  job,
  today,
  canIssue,
  onIssue,
  onRecall,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  canIssue: boolean;
  onIssue: () => void;
  onRecall: (order: MaintenanceWorkOrder) => void;
  onDone: () => void;
}) {
  const attended = useMutation(
    trpc.maintenance.attended.mutationOptions({
      meta: { errorTitle: 'Could not record that the contractor attended' },
      onSuccess: onDone,
    }),
  );
  const finished = useMutation(
    trpc.maintenance.finished.mutationOptions({
      meta: { errorTitle: 'Could not record that the work is finished' },
      onSuccess: onDone,
    }),
  );

  const open = job.state !== 'completed' && job.state !== 'cancelled';

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Work orders"
        subtitle={
          open && job.approvedLimitCents !== null ? (
            <>
              <Amount value={BigInt(job.remainingCents ?? '0')} currency tone="neutral" /> left to
              send
            </>
          ) : undefined
        }
        className="p-0 pb-3"
        aside={
          canIssue ? (
            <Button size="sm" onClick={onIssue}>
              Send a contractor
            </Button>
          ) : undefined
        }
      />

      {job.workOrderList.length === 0 ? (
        <p className="text-[0.8125rem] text-ink-muted">
          {job.approvedLimitCents === null && open
            ? 'Nobody sent. Authorise a spend first.'
            : 'Nobody sent.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {job.workOrderList.map((order) => (
            <li key={order.id} className="border-b border-rule pb-3 last:border-0 last:pb-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <Ref entity="work_order" sequence={order.refSeq} tone="strong" />
                  <Link
                    to="/maintenance/contractors/$ref"
                    params={{ ref: order.supplierRef }}
                    className="ml-2 font-medium hover:text-accent hover:underline"
                  >
                    {order.supplierName}
                  </Link>
                  <Chip tone={ORDER_TONE[order.state]} className="ml-2">
                    {WORK_ORDER_STATE_LABELS[order.state]}
                  </Chip>
                </div>
                <Amount
                  value={BigInt(order.authorisedCents)}
                  currency
                  tone={order.state === 'recalled' ? 'neutral' : 'accent'}
                />
              </div>

              <p className="mt-1 max-w-prose text-[0.8125rem] whitespace-pre-line text-ink-muted">
                {order.instructions}
              </p>

              <p className="mt-1 text-[0.75rem] text-ink-subtle">
                Issued {formatNzDate(order.issuedOn)}
                {order.issuedByName ? ` by ${order.issuedByName}` : ''}
                {order.scheduledFor ? ` · booked ${formatNzDate(order.scheduledFor)}` : ''}
                {order.attendedOn ? ` · attended ${formatNzDate(order.attendedOn)}` : ''}
                {order.completedOn ? ` · finished ${formatNzDate(order.completedOn)}` : ''}
              </p>

              {order.state === 'recalled' ? (
                <p className="mt-1 text-[0.75rem] text-ink-muted">
                  Recalled{order.recalledByName ? ` by ${order.recalledByName}` : ''} —{' '}
                  {order.recalledReason}
                </p>
              ) : null}

              {order.state === 'issued' || order.state === 'attended' ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {order.state === 'issued' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={attended.isPending}
                      onClick={() => attended.mutate({ workOrderId: order.id, on: today })}
                    >
                      Attended today
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={finished.isPending}
                    onClick={() => finished.mutate({ workOrderId: order.id, on: today })}
                  >
                    Finished today
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onRecall(order)}>
                    Recall
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** The last rung — the invoices that arrived against this job. */
function Invoices({ job }: { job: MaintenanceJobDetail }) {
  return (
    <Panel className="p-5">
      <PanelHeader
        title="Invoices"
        subtitle="Owners are charged when a bill is approved"
        className="p-0 pb-3"
        aside={
          <Link to="/bills/new">
            <Button size="sm" variant="secondary">
              <span aria-hidden="true">+</span> Add bill
            </Button>
          </Link>
        }
      />

      {job.billList.length === 0 ? (
        <p className="text-[0.8125rem] text-ink-muted">No invoices yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {job.billList.map((bill) => (
            <li
              key={bill.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <Link
                  to="/bills/$ref"
                  params={{ ref: bill.ref }}
                  className={
                    bill.rejected
                      ? 'font-medium text-ink-subtle line-through'
                      : 'font-medium hover:text-accent hover:underline'
                  }
                >
                  {bill.ref}
                </Link>
                <span className="text-[0.8125rem] text-ink-muted">
                  {' '}
                  {bill.supplierName} · {formatNzDate(bill.invoiceDate)}
                </span>
                <span className="block truncate text-[0.75rem] text-ink-subtle">
                  {bill.description}
                </span>
              </div>
              <Amount
                value={BigInt(bill.amountCents)}
                currency
                tone={bill.rejected ? 'neutral' : 'accent'}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * The first rung, and the one decision on this page an owner is shown if they dispute the repair —
 * so it stays on the page rather than behind a button. Nothing is posted: the owners are charged
 * when the contractor's invoice is approved.
 *
 * THE OWNER'S HALF APPEARS ONLY WHEN THE FIGURE CROSSES THE LINE, as the operator types rather
 * than after a refusal. `ownerApprovalRequired` is the SAME function the server refuses with — an
 * off-by-one between the two would show a form that submits into a refusal, or hide the owner's
 * half on a figure that needed it. A stale threshold in this tab can only ever produce a refusal
 * with a sentence, never an approval that should not have been taken.
 */
function Authorise({
  job,
  today,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  onDone: () => void;
}) {
  const [limit, setLimit] = useState('');
  const [ownerApprovedOn, setOwnerApprovedOn] = useState(today);
  const [ownerNote, setOwnerNote] = useState('');

  const approve = useMutation(
    trpc.maintenance.approve.mutationOptions({
      meta: { errorTitle: 'Could not approve that spend' },
      onSuccess: onDone,
    }),
  );

  const limitCents = parseMoney(limit);
  const threshold =
    job.ownerApprovalThresholdNowCents === null ? null : BigInt(job.ownerApprovalThresholdNowCents);
  const needsOwner = limitCents !== null && ownerApprovalRequired(threshold, limitCents);
  const ownerRecordReady =
    !needsOwner ||
    (ownerApprovedOn !== '' && ownerNote.trim().length >= MIN_OWNER_APPROVAL_NOTE_LENGTH);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (limitCents === null || limitCents <= 0n || !ownerRecordReady) return;
    approve.mutate({
      jobId: job.id,
      limitCents: limitCents.toString(),
      // Sent only when this tab believes it is needed. Whether it was REQUIRED is the server's
      // call: it re-reads the threshold, refuses if this was wrong, and keeps the note either way.
      ...(needsOwner
        ? { ownerApproval: { approvedOn: ownerApprovedOn, note: ownerNote.trim() } }
        : {}),
    });
  };

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Authorise the spend"
        subtitle="Trust role, with recent MFA"
        className="p-0 pb-3"
      />

      <form onSubmit={submit} noValidate className="flex flex-col gap-3">
        <Field
          label="Approve up to"
          required
          hint={
            threshold === null ? (
              'GST included'
            ) : (
              <>
                GST included · agency limit <Amount value={threshold} currency tone="neutral" />
                {job.ownerApprovalThresholdFromProperty ? ' (this owner’s term)' : ''}
              </>
            )
          }
        >
          {(props) => (
            <Input
              {...props}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              inputMode="decimal"
              className="tabular-nums"
              placeholder="850.00"
            />
          )}
        </Field>

        {needsOwner ? (
          <div className="flex flex-col gap-3 rounded-md border border-warn-soft bg-warn-soft/40 p-3">
            <p className="text-[0.8125rem] font-medium text-ink">
              Over the agency limit — record the owner’s agreement.
            </p>
            <Field label="Owner agreed on" required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={ownerApprovedOn}
                  max={today}
                  onChange={(e) => setOwnerApprovedOn(e.target.value)}
                />
              )}
            </Field>
            <Field label="What the owner said" required hint="Who, how, and to what figure">
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={ownerNote}
                  maxLength={MAX_OWNER_APPROVAL_NOTE_LENGTH}
                  onChange={(e) => setOwnerNote(e.target.value)}
                  placeholder="Rang J. Patel 4:15pm — agreed to $1,200 incl GST"
                />
              )}
            </Field>
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={
            approve.isPending || limitCents === null || limitCents <= 0n || !ownerRecordReady
          }
        >
          {approve.isPending ? (
            'Approving'
          ) : limitCents === null ? (
            'Approve'
          ) : (
            <>
              Approve up to <Amount value={limitCents} currency tone="neutral" />
            </>
          )}
        </Button>
      </form>
    </Panel>
  );
}

function QuoteDialog({
  job,
  today,
  onClose,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const suppliers = useQuery(trpc.suppliers.list.queryOptions(undefined, { staleTime: 30_000 }));
  const [supplierId, setSupplierId] = useState('');
  const [amount, setAmount] = useState('');
  const [quotedOn, setQuotedOn] = useState(today);
  const [validUntil, setValidUntil] = useState('');
  const [scope, setScope] = useState('');

  const record = useMutation(
    trpc.maintenance.quote.mutationOptions({
      meta: { errorTitle: 'Could not record that quote' },
      onSuccess() {
        onDone();
        onClose();
      },
    }),
  );

  const amountCents = parseMoney(amount);

  // A contractor already holding a live quote is left out: the server refuses a second, and the
  // right move is to withdraw the first, which is a button on the row.
  const quoted = new Set(
    job.quoteList.filter((quote) => quote.withdrawnAt === null).map((quote) => quote.supplierId),
  );
  const available = (suppliers.data ?? []).filter(
    (supplier) => supplier.status === 'active' && !quoted.has(supplier.id),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (supplierId === '' || amountCents === null || amountCents <= 0n || quotedOn === '') return;
    record.mutate({
      jobId: job.id,
      supplierId,
      amountCents: amountCents.toString(),
      quotedOn,
      ...(validUntil === '' ? {} : { validUntil }),
      ...(scope.trim() === '' ? {} : { scope: scope.trim() }),
    });
  };

  return (
    <FormDialog title="Add a quote" onClose={onClose} onSubmit={submit}>
      <SupplierSelect value={supplierId} onChange={setSupplierId} suppliers={available} />
      <Field label="Amount" required hint="GST included">
        {(props) => (
          <Input
            {...props}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            className="tabular-nums"
            placeholder="845.00"
          />
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Quoted on" required>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={quotedOn}
              onChange={(e) => setQuotedOn(e.target.value)}
            />
          )}
        </Field>
        <Field label="Holds until">
          {(props) => (
            <Input
              {...props}
              type="date"
              min={quotedOn}
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          )}
        </Field>
      </div>
      <Field label="Scope" hint="If it differs from the job">
        {(props) => (
          <Input
            {...props}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="Supply and install 180L cylinder"
          />
        )}
      </Field>
      <DialogActions pending={record.isPending} label="Add quote" onClose={onClose} />
    </FormDialog>
  );
}

/**
 * Sending a contractor.
 *
 * THE AUTHORISED FIGURE DEFAULTS TO A CHOSEN QUOTE and stays editable, because an agency routinely
 * orders a $780 quote with $850 of authority so the tradesperson can deal with what they find
 * under the floor without a second phone call. The ceiling is the instruction, not the price.
 */
function IssueOrderDialog({
  job,
  today,
  onClose,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const suppliers = useQuery(trpc.suppliers.list.queryOptions(undefined, { staleTime: 30_000 }));
  // One key per opening of this form: pressing Issue again after a lost reply repeats THIS order,
  // which the server answers with the first rather than sending the contractor twice (0113).
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [supplierId, setSupplierId] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [authorised, setAuthorised] = useState('');
  const [instructions, setInstructions] = useState('');
  const [issuedOn, setIssuedOn] = useState(today);
  const [scheduledFor, setScheduledFor] = useState('');

  const issue = useMutation(
    trpc.maintenance.issue.mutationOptions({
      meta: { errorTitle: 'Could not issue that work order' },
      onSuccess() {
        onDone();
        onClose();
      },
    }),
  );

  const remaining = BigInt(job.remainingCents ?? '0');
  const liveQuotes = job.quoteList.filter((quote) => quote.withdrawnAt === null && !quote.ordered);
  const theirQuotes = liveQuotes.filter((quote) => quote.supplierId === supplierId);
  const authorisedCents = parseMoney(authorised);
  const overAuthority = authorisedCents !== null && authorisedCents > remaining;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (
      supplierId === '' ||
      authorisedCents === null ||
      authorisedCents <= 0n ||
      overAuthority ||
      instructions.trim() === '' ||
      issuedOn === ''
    )
      return;
    issue.mutate({
      idempotencyKey,
      jobId: job.id,
      supplierId,
      authorisedCents: authorisedCents.toString(),
      instructions: instructions.trim(),
      issuedOn,
      ...(quoteId === '' ? {} : { quoteId }),
      ...(scheduledFor === '' ? {} : { scheduledFor }),
    });
  };

  return (
    <FormDialog title="Send a contractor" onClose={onClose} onSubmit={submit}>
      <SupplierSelect
        value={supplierId}
        onChange={(id) => {
          setSupplierId(id);
          // A quote belongs to one contractor; keeping it across a change would name somebody
          // else's price, which the server refuses.
          setQuoteId('');
        }}
        suppliers={(suppliers.data ?? []).filter((supplier) => supplier.status === 'active')}
      />

      {theirQuotes.length > 0 ? (
        <Field label="Against their quote">
          {(props) => (
            <Select
              {...props}
              value={quoteId}
              onChange={(e) => {
                setQuoteId(e.target.value);
                const chosen = theirQuotes.find((quote) => quote.id === e.target.value);
                // Pre-fill the ceiling from the price, and leave it editable.
                if (chosen) setAuthorised(formatAmount(BigInt(chosen.amountCents)));
              }}
            >
              <option value="">No quote</option>
              {theirQuotes.map((quote) => (
                <option key={quote.id} value={quote.id}>
                  ${formatAmount(BigInt(quote.amountCents))} · {formatNzDate(quote.quotedOn)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}

      <Field
        label="Authorise up to"
        required
        hint={`$${formatAmount(remaining)} left to send`}
        error={overAuthority ? `Only $${formatAmount(remaining)} is left` : undefined}
      >
        {(props) => (
          <Input
            {...props}
            value={authorised}
            onChange={(e) => setAuthorised(e.target.value)}
            inputMode="decimal"
            className="tabular-nums"
            placeholder={formatAmount(remaining)}
          />
        )}
      </Field>

      <Field label="Instructions" required hint="Sent to the contractor with the access notes">
        {(props) => (
          <Textarea
            {...props}
            rows={3}
            maxLength={MAX_WORK_ORDER_INSTRUCTIONS_LENGTH}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Replace the failed hot water cylinder. Ring before exceeding the limit."
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Issued on" required>
          {(props) => (
            <Input
              {...props}
              type="date"
              min={job.reportedOn}
              value={issuedOn}
              onChange={(e) => setIssuedOn(e.target.value)}
            />
          )}
        </Field>
        <Field label="Booked for">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          )}
        </Field>
      </div>

      <DialogActions pending={issue.isPending} label="Issue work order" onClose={onClose} />
    </FormDialog>
  );
}

function RecallDialog({
  order,
  onClose,
  onDone,
}: {
  order: MaintenanceWorkOrder;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const recall = useMutation(
    trpc.maintenance.recall.mutationOptions({
      meta: { errorTitle: 'Could not recall that work order' },
      onSuccess() {
        onDone();
        onClose();
      },
    }),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 10) return;
    recall.mutate({ workOrderId: order.id, reason: reason.trim() });
  };

  return (
    <FormDialog title={`Recall ${order.supplierName}`} onClose={onClose} onSubmit={submit}>
      <ReasonField
        label="Why they are being stood down"
        value={reason}
        onChange={setReason}
        placeholder="Owner has arranged their own tradesperson"
      />
      <DialogActions pending={recall.isPending} label="Recall order" onClose={onClose} />
    </FormDialog>
  );
}

/**
 * Signing off. A contractor who never rang back is usually the stale record, not the job, so open
 * orders are flagged rather than refused. Invoices can still be entered afterwards.
 */
function CompleteDialog({
  job,
  today,
  onClose,
  onDone,
}: {
  job: MaintenanceJobDetail;
  today: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [completedOn, setCompletedOn] = useState(today);
  const [notes, setNotes] = useState('');
  const complete = useMutation(
    trpc.maintenance.complete.mutationOptions({
      meta: { errorTitle: 'Could not complete that job' },
      onSuccess() {
        onDone();
        onClose();
      },
    }),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (completedOn === '') return;
    complete.mutate({
      jobId: job.id,
      completedOn,
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    });
  };

  return (
    <FormDialog title="Mark complete" onClose={onClose} onSubmit={submit}>
      {job.liveWorkOrders > 0 ? (
        <p className="text-[0.8125rem] text-warn">
          {job.liveWorkOrders} work order{job.liveWorkOrders === 1 ? '' : 's'} still open.
        </p>
      ) : null}
      <Field label="Completed on" required>
        {(props) => (
          <Input
            {...props}
            type="date"
            min={job.reportedOn}
            value={completedOn}
            onChange={(e) => setCompletedOn(e.target.value)}
          />
        )}
      </Field>
      <Field label="Notes">
        {(props) => (
          <Textarea
            {...props}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="New 180L cylinder fitted. Carpet dried."
          />
        )}
      </Field>
      <DialogActions pending={complete.isPending} label="Mark complete" onClose={onClose} />
    </FormDialog>
  );
}

/**
 * Calling it off. Nothing is posted; the reason stays on the record because it is what answers a
 * tenant asking why nobody came. A cancelled job cannot be revived — work that happens after all is
 * reported again, so the response target counts from when somebody actually asked.
 */
function CancelDialog({
  job,
  onClose,
  onDone,
}: {
  job: MaintenanceJobDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useMutation(
    trpc.maintenance.cancel.mutationOptions({
      meta: { errorTitle: 'Could not cancel that job' },
      onSuccess() {
        onDone();
        onClose();
      },
    }),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 10) return;
    cancel.mutate({ jobId: job.id, reason: reason.trim() });
  };

  return (
    <FormDialog title="Cancel job" onClose={onClose} onSubmit={submit}>
      {job.liveWorkOrders > 0 ? (
        <p className="text-[0.8125rem] text-warn">Recall the open work orders first.</p>
      ) : null}
      <ReasonField
        label="Reason"
        value={reason}
        onChange={setReason}
        placeholder="Duplicate of MN000041 — reported twice"
      />
      <DialogActions pending={cancel.isPending} label="Cancel job" onClose={onClose} />
    </FormDialog>
  );
}

function SupplierSelect({
  value,
  onChange,
  suppliers,
}: {
  value: string;
  onChange: (id: string) => void;
  suppliers: readonly { id: string; name: string; trade: keyof typeof SUPPLIER_TRADE_LABELS }[];
}) {
  return (
    <Field label="Contractor" required>
      {(props) => (
        <Select {...props} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose a contractor</option>
          {suppliers.map((supplier) => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.name} — {SUPPLIER_TRADE_LABELS[supplier.trade]}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

// The server wants at least ten characters; the hint says so only once somebody has started.
function ReasonField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const short = value.trim().length > 0 && value.trim().length < 10;
  return (
    <Field label={label} required error={short ? 'At least 10 characters' : undefined}>
      {(props) => (
        <Textarea
          {...props}
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </Field>
  );
}

function parseMoney(raw: string): Money | null {
  if (raw.trim() === '') return null;
  try {
    return parseAmount(raw);
  } catch {
    return null;
  }
}

function NotFound({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Maintenance job"
      title={title}
      back={{ label: 'Back to maintenance', link: { to: '/maintenance' } }}
    >
      {children}
    </RecordNotFound>
  );
}
